import type { ReactNode } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import { PaperLoader } from "src/components/loading";
import { Navigate, useLocation, useMatch, useParams } from "src/router";
import type { RouteObject } from "src/router";
import { lazyNamed, lazyWithRetry } from "src/utils/lazy-named";
import {
  DrawerRouteGuard,
  MetadataDrawerRouteGuard,
} from "src/cep/shared/drawers/drawer-route-guard";
import { prefetchContentBlocks } from "./hooks/prefetch-content-blocks";
import { useMessageOutletContext } from "./message/outlet";
import { useMessageSendTabs } from "./message/send-tabs";

const MessagesLoader = lazyWithRetry(() => import("./messages"));
const MessageDetailLoader = lazyWithRetry(() => import("./message-detail"));
const MessageOverviewPage = lazyNamed(
  () => import("./message/message-overview-page"),
  "MessageOverviewPage",
);
// All message-detail drawers funnel through a single barrel so Vite/Rollup
// emits one shared chunk. The message-detail page also statically imports the
// barrel, making the drawer chunk a preload of the page chunk — by the time
// the user clicks any drawer trigger, the code is already in memory.
const drawers = () => import("./message/drawers");
const MetadataDrawer = lazyNamed(drawers, "MetadataDrawer");
const SendLimitsDrawer = lazyNamed(drawers, "SendLimitsDrawer");

const MessageReportingPage = lazyNamed(
  () => import("./message/message-reporting-page"),
  "MessageReportingPage",
);
const MessageRecipientsPage = lazyNamed(
  () => import("./message/message-recipients-page"),
  "MessageRecipientsPage",
);

const MessageContentSurface = lazyNamed(
  () => import("./message/message-content-surface"),
  "MessageContentSurface",
);

// With labels on, the Campaigns list holds journey messages, so this list redirects there.
// FeatureFlagRoute only redirects while a flag is off, so this inverse gate stays inline.
const MessagesListRoute = () => {
  const { cepLabels } = useFlags();
  return cepLabels ? (
    <Navigate to="/native-delivery/campaigns" replace />
  ) : (
    <MessagesLoader />
  );
};

// With labels on, old links move under Campaigns, keeping their drawer path, query, and state.
const MessageDetailRoute = () => {
  const { cepLabels } = useFlags();
  const location = useLocation();
  const { messageId } = useParams<{ messageId: string }>();
  const tail = useMatch("/native-delivery/messages/:messageId/*")?.params["*"];

  if (!cepLabels) return <MessageDetailLoader />;

  return (
    <Navigate
      to={{
        pathname: `/native-delivery/campaigns/messages/${messageId}${tail ? `/${tail}` : ""}`,
        search: location.search,
      }}
      state={location.state}
      replace
    />
  );
};

function MessageSendTabRoute({ children }: { children: ReactNode }) {
  const { message, journeyState } = useMessageOutletContext();
  const { showSendTabs, isLoading } = useMessageSendTabs({
    messageId: String(message.id),
    journeyState,
  });

  // Hold the frame rather than redirecting: the count starts at zero, and a
  // redirect would unmount the component that would re-read it.
  if (isLoading) return <PaperLoader />;

  if (!showSendTabs) return <Navigate to="../overview" replace />;

  return <>{children}</>;
}

const messageDetailChildren: RouteObject[] = [
  {
    index: true,
    spacesAccess: "hidden-from-limited-view",
    element: <Navigate to="overview" replace />,
  },
  {
    path: "reporting",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <MessageSendTabRoute>
        <MessageReportingPage />
      </MessageSendTabRoute>
    ),
  },
  {
    path: "recipients",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <MessageSendTabRoute>
        <MessageRecipientsPage />
      </MessageSendTabRoute>
    ),
  },
  {
    path: "overview",
    spacesAccess: "hidden-from-limited-view",
    element: <MessageOverviewPage />,
    children: [
      {
        path: "metadata",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <MetadataDrawerRouteGuard>
            <MetadataDrawer />
          </MetadataDrawerRouteGuard>
        ),
      },
      {
        path: "content",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <DrawerRouteGuard>
            <MessageContentSurface />
          </DrawerRouteGuard>
        ),
      },
      {
        // Parity with the campaign host, so a link to a message's Localize
        // tab resolves from either page. Sibling, so `..` reaches overview.
        path: "content/localize",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <DrawerRouteGuard>
            <MessageContentSurface initialTab="localize" />
          </DrawerRouteGuard>
        ),
      },
      {
        path: "send-limits",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <DrawerRouteGuard isAllowed={(s) => s.canEditStructure}>
            <SendLimitsDrawer />
          </DrawerRouteGuard>
        ),
      },
    ],
  },
];

export const routes: RouteObject[] = [
  {
    path: "native-delivery/messages",
    spacesAccess: "hidden-from-limited-view",
    element: <MessagesListRoute />,
  },
  {
    path: "native-delivery/messages/:messageId",
    spacesAccess: "hidden-from-limited-view",
    element: <MessageDetailRoute />,
    loader: prefetchContentBlocks,
    children: messageDetailChildren,
  },
  {
    path: "native-delivery/campaigns/messages/:messageId",
    spacesAccess: "hidden-from-limited-view",
    element: <MessageDetailLoader />,
    loader: prefetchContentBlocks,
    children: messageDetailChildren,
  },
];
